/**
 * Handling for partner-supplied HTML.
 *
 * Partners send arbitrary markup with their own inline styles, so anything that
 * could execute or restyle the page is stripped before it renders.
 */

const ALLOWED_TCPA_TAGS = /^(a|b|strong|i|em|u|br|p|span|sup)$/i

/**
 * Strips scripts, event handlers, and inline styles from TCPA markup while
 * keeping the links and emphasis the disclosure legally needs.
 */
export function sanitizeTcpaHtml(html: string): string {
  if (!html) return ''
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '')

  const container = document.createElement('div')
  container.innerHTML = html

  const walk = (node: Element) => {
    Array.from(node.children).forEach((child) => {
      if (!ALLOWED_TCPA_TAGS.test(child.tagName)) {
        // Keep the text, drop the element.
        child.replaceWith(...Array.from(child.childNodes))
        return
      }
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase()
        const isSafeLink =
          child.tagName.toLowerCase() === 'a' &&
          (name === 'href' || name === 'target' || name === 'rel')
        if (!isSafeLink) child.removeAttribute(attr.name)
      })
      if (child.tagName.toLowerCase() === 'a') {
        const href = child.getAttribute('href') || ''
        if (/^\s*javascript:/i.test(href)) child.removeAttribute('href')
        child.setAttribute('target', '_blank')
        child.setAttribute('rel', 'noopener noreferrer')
      }
      walk(child)
    })
  }
  walk(container)
  return container.innerHTML
}

/**
 * Pulls plain-text selling points out of partner body markup.
 *
 * Partners send between one and eight bullets; the caller caps the count so the
 * card height doesn't track how verbose a partner is.
 */
export function extractListItems(html: string): string[] {
  if (!html) return []
  if (typeof document === 'undefined') return []

  const container = document.createElement('div')
  container.innerHTML = html

  const items = Array.from(container.querySelectorAll('li'))
    .map((li) => li.textContent?.replace(/\s+/g, ' ').trim() || '')
    .filter(Boolean)
  if (items.length > 0) return items

  // No list markup — fall back to paragraphs, then line breaks.
  const paragraphs = Array.from(container.querySelectorAll('p'))
    .map((p) => p.textContent?.replace(/\s+/g, ' ').trim() || '')
    .filter(Boolean)
  if (paragraphs.length > 1) return paragraphs

  return (container.textContent || '')
    .split(/\r?\n|\s\|\s/)
    .map((line) => line.replace(/^[•\-*]\s*/, '').trim())
    .filter(Boolean)
}
